/**
 * Shared helpers + tiny components used across the Admin panel modules.
 * Pure refactor — every export is byte-for-byte equivalent to the symbol
 * it replaces inside the legacy Admin.jsx monolith.
 */
import React from "react";

/** Great-circle distance in km — mirrors the backend's haversine_km. */
export const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const kmToMi = (km) => km * 0.621371;

/** Colour-codes the dispatch-stage label rendered under each ride row. */
export const dispatchStageColor = (stage) => {
  switch (stage) {
    case "no_drivers_online":
      return "#EF4444"; // red — operator must act
    case "no_eligible_drivers":
      return "#F97316"; // orange — degraded
    case "offered":
      return "#F59E0B"; // amber — in flight
    case "assigned":
      return "#10B981"; // green — handled
    case "searching":
    default:
      return "#6B7280"; // neutral
  }
};

/** Compact status pill rendered on every ride row. */
export const StatusPill = ({ status }) => {
  const map = {
    searching: { c: "#F59E0B", l: "SEARCHING" },
    offered: { c: "#F59E0B", l: "OFFERED" },
    accepted: { c: "#10B981", l: "ACCEPTED" },
    in_progress: { c: "#002FA7", l: "IN PROGRESS" },
    completed: { c: "#10B981", l: "COMPLETED" },
    cancelled: { c: "#9CA3AF", l: "CANCELLED" },
  };
  const s = map[status] || { c: "#0A0A0A", l: status };
  return (
    <span className="inline-flex items-center gap-1.5 label-mini whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.c }} />
      {s.l}
    </span>
  );
};

/** Sticky-stat chip displayed in the admin top strip. */
export const StatChip = ({ icon, label, value, accent, pulse, highlight }) => (
  <div className={`bg-white px-4 py-3 flex-1 min-w-[120px] ${highlight ? "bg-[#FAFAFB]" : ""}`}>
    <div className="flex items-center gap-1.5 mb-1">
      {pulse && <span className="w-1.5 h-1.5 rounded-full pulse-warning" style={{ backgroundColor: accent || "#0A0A0A" }} />}
      <div style={{ color: accent || "#0A0A0A" }}>
        {React.cloneElement(icon, { className: "w-3.5 h-3.5", strokeWidth: 2 })}
      </div>
      <div className="label-mini text-muted-foreground text-[10px]">{label}</div>
    </div>
    <div className="font-heading text-xl md:text-2xl font-black tracking-tight">{value}</div>
  </div>
);

/** Compact label/value row used inside MarkerInfoCard. */
export const Row = ({ label, value, valueColor }) => (
  <div className="flex items-baseline justify-between gap-3 text-xs">
    <span className="label-mini text-muted-foreground flex-shrink-0">{label}</span>
    <span
      className="font-semibold text-right truncate"
      style={valueColor ? { color: valueColor } : undefined}
    >
      {value}
    </span>
  </div>
);
